import { useEffect, useMemo, useRef } from 'react'
import { createNadDebug } from '@ponpon/api-client'
import type { AudioEditClip } from './audioEditTypes'
import { getAudioEditDurationS, getAudioEditSourceTimeS } from './audioEditMath'

const debug = createNadDebug('dw:timeline-playback:audio-pool')
const globalMuteLog = createNadDebug('dw:videoeditor-canvas-preview:audio-pool')
const MAX_DRIFT_SECONDS = 0.1

declare global {
  interface HTMLAudioElement {
    webkitPreservesPitch?: boolean
  }
}

interface AudioPoolEntry {
  element: HTMLAudioElement
  clipId: string
}

interface UseAudioClipPoolProps {
  clips: AudioEditClip[]
  playheadPosition: number
  isPlaying: boolean
  resolveClipUrl: (clip: AudioEditClip) => string | null
  /**
   * When true, every pooled <audio> element is forced to muted=true regardless
   * of per-clip mute. Used by the canvas preview's global mute button.
   */
  globalMuted?: boolean
}

function setPitchPreservation(element: HTMLAudioElement, preservePitch: boolean) {
  element.preservesPitch = preservePitch
  element.webkitPreservesPitch = preservePitch
}

export function useAudioClipPool({
  clips,
  playheadPosition,
  isPlaying,
  resolveClipUrl,
  globalMuted = false,
}: UseAudioClipPoolProps): void {
  const poolRef = useRef<Map<string, AudioPoolEntry>>(new Map())
  const fingerprint = useMemo(
    () => clips.map((c) => `${c.id}:${c.sourcePath ?? c.sourceUrl ?? ''}:${c.muted}`).join('|'),
    [clips],
  )
  const fingerprintRef = useRef('')
  const lastGlobalMutedRef = useRef<boolean | null>(null)

  // Apply global mute to every live element.
  useEffect(() => {
    const pool = poolRef.current
    let appliedCount = 0
    for (const [, entry] of pool) {
      if (entry.element.muted !== globalMuted) {
        entry.element.muted = globalMuted
        appliedCount += 1
      }
    }
    if (lastGlobalMutedRef.current !== globalMuted) {
      globalMuteLog({
        event: 'global-mute-applied',
        muted: globalMuted,
        elementsTouched: appliedCount,
        totalElements: pool.size,
      })
      lastGlobalMutedRef.current = globalMuted
    }
  }, [globalMuted, clips])

  useEffect(() => {
    if (fingerprintRef.current === fingerprint) return
    fingerprintRef.current = fingerprint

    const pool = poolRef.current
    const currentClipIds = new Set(clips.map((clip) => clip.id))

    for (const [clipId, entry] of pool) {
      if (!currentClipIds.has(clipId)) {
        debug({ event: 'audio-pool-remove', clipId })
        entry.element.pause()
        entry.element.src = ''
        pool.delete(clipId)
      }
    }

    for (const clip of clips) {
      if (clip.muted) {
        const existing = pool.get(clip.id)
        if (existing) {
          existing.element.pause()
          existing.element.src = ''
          pool.delete(clip.id)
        }
        continue
      }

      if (!pool.has(clip.id)) {
        const src = resolveClipUrl(clip)
        if (!src) continue
        const element = document.createElement('audio')
        element.preload = 'auto'
        element.src = src
        element.volume = clip.volume
        element.playbackRate = clip.speed
        element.muted = globalMuted
        setPitchPreservation(element, clip.preservePitch)
        pool.set(clip.id, { element, clipId: clip.id })
      }
    }
  }, [clips, fingerprint, resolveClipUrl, globalMuted])

  useEffect(() => {
    const pool = poolRef.current
    for (const clip of clips) {
      if (clip.muted || pool.has(clip.id)) continue
      const src = resolveClipUrl(clip)
      if (!src) continue
      const element = document.createElement('audio')
      element.preload = 'auto'
      element.src = src
      element.volume = clip.volume
      element.playbackRate = clip.speed
      element.muted = globalMuted
      setPitchPreservation(element, clip.preservePitch)
      pool.set(clip.id, { element, clipId: clip.id })
    }
  }, [clips, resolveClipUrl, globalMuted])

  useEffect(() => {
    const pool = poolRef.current

    for (const clip of clips) {
      if (clip.muted) continue
      const entry = pool.get(clip.id)
      if (!entry) continue

      const { element } = entry
      const clipEnd = clip.timelineStartS + getAudioEditDurationS(clip)
      const isInRange = playheadPosition >= clip.timelineStartS && playheadPosition < clipEnd

      if (!isInRange) {
        if (!element.paused) element.pause()
        continue
      }

      if (element.volume !== clip.volume) element.volume = clip.volume
      if (element.playbackRate !== clip.speed) element.playbackRate = clip.speed
      if (element.muted !== globalMuted) element.muted = globalMuted
      setPitchPreservation(element, clip.preservePitch)

      const expectedTime = getAudioEditSourceTimeS(clip, playheadPosition)
      if (Math.abs(element.currentTime - expectedTime) > MAX_DRIFT_SECONDS) {
        debug({
          event: 'audio-pool-drift-correction',
          clipId: clip.id,
          currentTimeS: Number(element.currentTime.toFixed(3)),
          expectedTimeS: Number(expectedTime.toFixed(3)),
          speed: Number(clip.speed.toFixed(3)),
        })
        element.currentTime = expectedTime
      }

      if (isPlaying && element.paused) {
        element.play().catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('Audio play failed:', err)
          }
        })
      } else if (!isPlaying && !element.paused) {
        element.pause()
      }
    }
  }, [clips, playheadPosition, isPlaying, globalMuted])

  useEffect(() => {
    const pool = poolRef.current
    return () => {
      for (const [, entry] of pool) {
        entry.element.pause()
        entry.element.src = ''
      }
      pool.clear()
    }
  }, [])
}
